import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PersonalizedRecommendations = ({ 
  recommendations, 
  onProductClick, 
  onRefreshRecommendations,
  userGoals,
  userTier 
}) => {
  const getRecommendationReason = (product) => {
    const reasons = [
      `Based on your ${userGoals?.[0]} goal`,
      `Popular with ${userTier} members`,
      `Matches your wellness preferences`,
      `Recommended by your center`,
      `Similar to your recent purchases`
    ];
    return product?.recommendationReason || reasons?.[Math.floor(Math.random() * reasons?.length)];
  };

  if (!recommendations || recommendations?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-wellness p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Sparkles" size={20} className="text-muted-foreground" />
            <h3 className="font-heading font-semibold text-foreground">Recommended for You</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefreshRecommendations}
            iconName="RefreshCw"
          />
        </div>
        <div className="text-center py-8">
          <Icon name="Target" size={48} className="text-muted mx-auto mb-3" />
          <p className="text-muted-foreground">No recommendations available</p>
          <p className="text-sm text-muted-foreground mt-1">
            Complete your profile to get personalized suggestions
          </p>
          <Button variant="outline" size="sm" className="mt-3">
            Update Profile
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-wellness p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Sparkles" size={20} className="text-accent" />
          <h3 className="font-heading font-semibold text-foreground">Recommended for You</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefreshRecommendations}
          iconName="RefreshCw"
          iconPosition="left"
        >
          Refresh
        </Button>
      </div>
      {/* User Context */}
      {userGoals && userGoals?.length > 0 && (
        <div className="mb-4 p-3 bg-accent/5 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Target" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent-foreground">
              Based on your wellness goals
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {userGoals?.slice(0, 3)?.map((goal, index) => (
              <span
                key={index}
                className="text-xs bg-accent/10 text-accent-foreground px-2 py-1 rounded-full"
              >
                {goal}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-4">
        {recommendations?.slice(0, 4)?.map((product) => (
          <div
            key={product?.id}
            onClick={() => onProductClick?.(product)}
            className="flex items-start space-x-4 p-3 rounded-lg hover:bg-muted transition-wellness cursor-pointer group"
          >
            <div className="relative flex-shrink-0">
              <Image
                src={product?.image}
                alt={product?.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
              {product?.matchScore && product?.matchScore > 90 && (
                <div className="absolute -top-1 -right-1 bg-success text-success-foreground text-xs px-1.5 py-0.5 rounded-full font-medium">
                  {product?.matchScore}%
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground group-hover:text-primary transition-wellness line-clamp-1">
                {product?.name}
              </h4>
              
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {product?.description}
              </p>

              {/* Recommendation Reason */}
              <div className="flex items-center space-x-2 mt-2">
                <Icon name="Lightbulb" size={12} className="text-accent" />
                <span className="text-xs text-accent-foreground">
                  {getRecommendationReason(product)}
                </span>
              </div>

              {/* Product Details */}
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={12}
                      className={i < Math.floor(product?.rating) ? 'text-accent fill-current' : 'text-muted'}
                    />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({product?.reviewCount})
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {product?.centerName}
                </span>
              </div>
            </div>

            <div className="flex flex-col items-end space-y-2">
              <div className="text-right">
                <span className="font-semibold text-foreground">
                  ${product?.price}
                </span>
                {product?.originalPrice && product?.originalPrice > product?.price && (
                  <div className="text-xs text-muted-foreground line-through">
                    ${product?.originalPrice}
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                size="xs"
                onClick={(e) => {
                  e?.stopPropagation();
                  onProductClick?.(product);
                }}
                iconName="Plus"
              >
                Add
              </Button>
            </div>
          </div>
        ))}
      </div>
      {recommendations?.length > 4 && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" size="sm" className="w-full">
            View All Recommendations
          </Button>
        </div>
      )}
      {/* Feedback Section */}
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground mb-2">
          How are these recommendations?
        </p>
        <div className="flex space-x-2">
          <Button variant="ghost" size="xs" iconName="ThumbsUp">
            Helpful
          </Button>
          <Button variant="ghost" size="xs" iconName="ThumbsDown">
            Not Relevant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalizedRecommendations;